import React, { useEffect, useState } from "react";
import * as Pi from "@phosphor-icons/react";
import { PageLoader, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";

const MODEL_LABELS = {
  fixed: "Fixed", commission: "% commission", per_ticket: "per ticket", per_attendee: "per attendee",
  per_event: "per event", per_day: "per day", subscription: "/month", package: "package",
  quotation: "on quotation", starting: "starting from", quantity: "quantity-based",
};

function priceLabel(s) {
  if (s.pricing_model === "commission") return "Commission";
  if (s.pricing_model === "quotation" || s.price === 0) return "Custom quote";
  return `${money(s.price)} ${MODEL_LABELS[s.pricing_model] || ""}`.trim();
}

export default function Services() {
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([api.get("/services"), api.get("/organizer/service-orders")])
    .then(([s, o]) => { setServices(s.data); setOrders(o.data.orders); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const inquire = (s) => {
    window.dispatchEvent(new CustomEvent("eventra:open-chat", {
      detail: {
        subject: `Inquiry: ${s.name}`,
        context: { service: s.name, service_id: s.id, inquiry_type: "service_inquiry" },
        message: `Hi, I'd like to know more about "${s.name}". Could you share pricing details and how to get started?`,
      },
    }));
    toast.success("Opening live chat — ask us anything about this service.");
  };

  if (loading) return <PageLoader />;
  const cats = [...new Set(services.map((s) => s.category))];

  return (
    <div>
      <div className="overline mb-1">Grow your event</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Services & Pricing</h1>

      <Tabs defaultValue="catalogue">
        <TabsList className="mb-6"><TabsTrigger value="catalogue" data-testid="tab-catalogue">Catalogue</TabsTrigger><TabsTrigger value="orders" data-testid="tab-orders">My Orders ({orders.length})</TabsTrigger></TabsList>

        <TabsContent value="catalogue" className="space-y-10">
          {cats.map((cat) => (
            <div key={cat}>
              <div className="overline mb-3">{cat}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {services.filter((s) => s.category === cat).map((s) => {
                  const Icon = Pi[s.icon] || Pi.Sparkle;
                  return (
                    <div key={s.id} data-testid={`service-${s.id}`} className="rounded-xl border border-border bg-card p-5 flex flex-col card-lift">
                      <div className="flex items-center justify-between">
                        <span className="w-11 h-11 grid place-items-center rounded-lg bg-primary/10 text-primary"><Icon size={22} weight="fill" /></span>
                        {s.featured && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/25">Popular</span>}
                      </div>
                      <h3 className="font-heading font-semibold text-lg mt-3">{s.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1 flex-1">{s.short_description}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="font-heading font-semibold">{priceLabel(s)}<div className="text-xs text-muted-foreground font-normal">+ {s.gst_percent}% GST</div></div>
                        <Button size="sm" onClick={() => inquire(s)} className="rounded-full" data-testid={`inquire-${s.id}`}><Pi.ChatCircleText size={15} className="mr-1" /> Inquire</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="orders">
          {orders.length === 0 ? <p className="text-sm text-muted-foreground">No purchases yet.</p> : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="rounded-xl border border-border bg-card p-4" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">{o.items.map((i) => i.name).join(", ")}</div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="text-sm text-muted-foreground flex justify-between">
                    <span>{new Date(o.created_at).toLocaleDateString("en-IN")}</span>
                    <span className="font-semibold text-foreground">{money(o.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
